/**
 * Typed API functions, one per backend endpoint.
 *
 * Grouped by resource. Query keys for TanStack Query live alongside them so a mutation and the
 * queries it invalidates cannot drift apart.
 */
import {
  downloadFile,
  fetchBlob,
  getCollection,
  getItem,
  getList,
  openFile,
  printFile,
  request,
  type Paged,
} from '@/api/client';
import type {
  BatchProgress,
  DeckFileKind,
  DeckTextEdit,
  AddedSlideInput,
  WorkbookSheet,
  WorkbookReading,
  WorkbookKind,
  WorkbookGrid,
  ReadingCorrectionInput,
  DeckOptions,
  DeckTemplate,
  PresentationDeck,
  PresentationDeckDetail,
  EmployerSync,
  EmployerSyncOverview,
  Application,
  Assignment,
  AssignmentActivity,
  AuditEvent,
  AuthMethods,
  BenefitLine,
  Board,
  Carrier,
  CarrierContact,
  CarrierDetail,
  CensusBatch,
  CensusBatchDetail,
  CensusBatchSummary,
  CensusCarrierChoice,
  CensusCarrierLayout,
  CensusField,
  CensusLayoutDefinition,
  CensusMember,
  Clarification,
  ComparisonScope,
  CurrentUser,
  DocumentFormat,
  DueDateGuidance,
  EligibilityVerdict,
  Employer,
  ExtractionJob,
  ExtractionJobDetail,
  ExtractionJobSummary,
  ExtractionPlan,
  PlacedByName,
  ExtractionTemplate,
  FlagResolution,
  FollowUp,
  FormFireImport,
  SpreadsheetImport,
  SpreadsheetInspect,
  InferredLayout,
  InternalDashboard,
  LoginResult,
  ModelKeyCheck,
  ModelKeyStatus,
  PersonSummary,
  ReadAccuracy,
  ReferenceData,
  ReviewStatus,
  Rfp,
  RfpDetail,
  RfpLine,
  RfpLineStatus,
  RfpSummary,
  SubmissionRoute,
  SubmissionRule,
  TemplateFamily,
  TrenchingAdjustment,
  TrenchingArtifact,
  TrenchingArtifactKind,
  TrenchingAssumption,
  TrenchingEngagement,
  TrenchingEngagementDetail,
  TrenchingEngagementSummary,
  TrenchingMember,
  TrenchingMemberItem,
  TrenchingTemplate,
  User,
  Workload,
  Packet,
  PacketDetail,
  PacketFieldFormat,
  PacketHtmlSample,
  PacketLibraryPage,
  PacketPageLayout,
  PacketLibrary,
  PacketListParams,
} from '@/types';

// --- query keys -------------------------------------------------------------

export const keys = {
  me: ['me'] as const,
  authMethods: ['auth', 'methods'] as const,
  reference: ['reference'] as const,
  employerSync: ['employer-sync'] as const,
  carrierSync: ['carrier-sync'] as const,
  platformApps: () => ['platform', 'apps'] as const,
  modelKey: ['platform', 'model-key'] as const,
  employers: (params?: unknown) => ['employers', params ?? null] as const,
  employer: (id: string) => ['employer', id] as const,
  employerRfps: (id: string) => ['employer', id, 'rfps'] as const,
  employerParticipation: (id: string) => ['employer', id, 'participation'] as const,
  carriers: (params?: unknown) => ['carriers', params ?? null] as const,
  carrier: (id: string) => ['carrier', id] as const,
  carrierContacts: (id: string) => ['carrier', id, 'contacts'] as const,
  carrierRoutes: (id: string) => ['carrier', id, 'routes'] as const,
  rfps: (params?: unknown) => ['rfps', params ?? null] as const,
  rfp: (id: string) => ['rfp', id] as const,
  rfpCarriers: (id: string) => ['rfp', id, 'carriers'] as const,
  rfpCreators: () => ['rfps', 'creators'] as const,
  rfpLineOwners: () => ['rfps', 'line-owners'] as const,
  rfpEligibility: (id: string, line?: BenefitLine) =>
    ['rfp', id, 'eligibility', line ?? null] as const,
  rfpLines: (id: string) => ['rfp', id, 'lines'] as const,
  /** Open lines per owner. Its own key: the board reads a page of RFPs, this reads every open
   *  line in the tenant, and they are not the same list. */
  rfpWorkload: () => ['rfps', 'workload'] as const,
  dueDateGuidance: () => ['rfps', 'due-date-guidance'] as const,
  activity: (rfpId: string, carrierId: string, line: BenefitLine) =>
    ['rfp', rfpId, 'activity', carrierId, line] as const,
  board: (params?: unknown) => ['board', params ?? null] as const,
  cellClarifications: (id: string, carrierId: string, line: string) =>
    ['rfp', id, 'cell', carrierId, line, 'clarifications'] as const,
  responses: (params?: unknown) => ['responses', params ?? null] as const,
  response: (id: string) => ['response', id] as const,
  responseNotes: (id: string) => ['response', id, 'notes'] as const,
  clarifications: (id: string) => ['response', id, 'clarifications'] as const,
  internalDashboard: (params?: unknown) => ['dashboard', 'internal', params ?? null] as const,
  followUps: (days: number) => ['dashboard', 'followUps', days] as const,
  proposalFormats: () => ['proposals', 'formats'] as const,
  /** The two comparisons a workbook can be the layout for — not the document formats above.
   *  Its own key because a key is a cache slot: sharing one with `proposalFormats` meant the
   *  library rendered whichever list had been fetched first, so arriving from a comparison
   *  showed a row per document format and only a reload showed the right screen. */
  proposalComparisonKinds: () => ['proposals', 'comparison-kinds'] as const,
  proposalJobs: (params?: unknown) => ['proposals', 'jobs', params ?? null] as const,
  proposalJob: (id: string) => ['proposals', 'job', id] as const,
  proposalJobBatch: (id: string) => ['proposals', 'job', id, 'batch'] as const,
  proposalTemplates: () => ['proposals', 'templates'] as const,
  /** The models sold proposals can be read with; empty without an API key. */
  proposalReadModels: () => ['proposals', 'read-models'] as const,
  proposalReadAccuracy: (source: string) => ['proposals', 'read-accuracy', source] as const,
  proposalBatchReads: () => ['proposals', 'batch-reads'] as const,
  /** The template library's own list: the same rows, carrying the kind it groups them by.
   *  Its own key for the same reason — the screens that *pick* a template cache the API's shape
   *  under `proposalTemplates`, and a row with no kind groups under nothing, so every comparison
   *  read 'None yet' until a reload. Still under the `proposals` prefix, so a mutation anywhere
   *  in the application refetches it. */
  proposalTemplateLibrary: () => ['proposals', 'template-library'] as const,
  packets: (params?: unknown) => ['packets', 'list', params ?? null] as const,
  packet: (id: string) => ['packets', 'packet', id] as const,
  packetLibrary: () => ['packets', 'library'] as const,
  packetHtmlSamples: () => ['packets', 'html-samples'] as const,
  packetLibraryPages: () => ['packets', 'library-pages'] as const,
  /** The same pages, shaped for the shared template screen: not the API's shape, so its own key. */
  packetLibraryPagesScreen: () => ['packets', 'library-pages', 'screen'] as const,
  packetLibraryPageKinds: () => ['packets', 'library-page-kinds'] as const,
  packetReviewers: (clientId: string) => ['packets', 'reviewers', clientId] as const,
  packetAssignees: () => ['packets', 'assignees'] as const,
  presentationDecks: (params?: unknown) => ['presentations', 'decks', params ?? null] as const,
  presentationDeck: (id: string) => ['presentations', 'deck', id] as const,
  presentationWorkbookSheets: (id: string) => ['presentations', 'deck', id, 'sheets'] as const,
  /** Keyed by generation: a replaced workbook regenerates the deck, and is read afresh. */
  presentationReading: (id: string, generation: number) =>
    ['presentations', 'deck', id, 'reading', generation] as const,
  presentationGrid: (id: string, kind: string, tab: string, generation: number) =>
    ['presentations', 'deck', id, 'grid', kind, tab, generation] as const,
  /** Without an employer: everybody who can work a deck the user could see — a filter's options. */
  presentationAssignees: (clientId?: string) => ['presentations', 'assignees', clientId ?? ''] as const,
  presentationReviewers: (clientId: string) => ['presentations', 'reviewers', clientId] as const,
  /** The API's shape, for the deck forms' template picker. */
  /** The library screen's shape — its own key, as `trenchingTemplateLibrary` explains. */
  presentationHtmlFamilies: () => ['presentations', 'html-families'] as const,
  presentationHtmlTemplateLibrary: () => ['presentations', 'html-template-library'] as const,
  trenchingEngagements: (params?: unknown) => ['trenching', 'engagements', params ?? null] as const,
  trenchingEngagement: (id: string) => ['trenching', 'engagement', id] as const,
  trenchingAssumptions: () => ['trenching', 'assumptions'] as const,
  trenchingFamilies: () => ['trenching', 'families'] as const,
  trenchingTemplates: () => ['trenching', 'templates'] as const,
  /** As `proposalTemplateLibrary`, and for the same reason: `EngagementNewPage` caches the
   *  API's shape under `trenchingTemplates`. */
  trenchingTemplateLibrary: () => ['trenching', 'template-library'] as const,
  trenchingMembers: (id: string, params?: unknown) =>
    ['trenching', 'engagement', id, 'members', params ?? null] as const,
  trenchingPredecessors: (clientId: string, ordinal: number) =>
    ['trenching', 'predecessors', clientId, ordinal] as const,
  trenchingMemberItems: (id: string, memberId: string) =>
    ['trenching', 'engagement', id, 'members', memberId, 'items'] as const,
  trenchingAdjustments: (id: string) => ['trenching', 'engagement', id, 'adjustments'] as const,
  censusBatches: (params?: unknown) => ['census', 'batches', params ?? null] as const,
  censusBatch: (id: string) => ['census', 'batch', id] as const,
  /** Every carrier layout and whether its template is here. Under the `census` prefix, so an
   *  upload refetches the pickers that read `templateReady` from it. */
  censusLayouts: () => ['census', 'layouts'] as const,
  /** The layouts this tenant defined itself, as against the ten written in code. */
  censusDefinedLayouts: () => ['census', 'layouts', 'defined'] as const,
  censusDefinedLayout: (id: string) => ['census', 'layout', id] as const,
  /** Stable: the fields a column can map to change when the application does, not when a
   *  tenant does. */
  censusFields: () => ['census', 'fields'] as const,
  /** Every carrier and whether a census can be produced for it, read before a census
   *  exists — choosing the carriers is part of starting one. */
  censusCarriers: () => ['census', 'carriers'] as const,
  users: (params?: unknown) => ['users', params ?? null] as const,
  user: (id: string) => ['user', id] as const,
  audit: (params?: unknown) => ['audit', params ?? null] as const,
};

// --- auth -------------------------------------------------------------------

export const auth = {
  /** What this deployment offers. No token: the sign-in page needs it before there is one. */
  methods: () => getItem<AuthMethods>('/auth/methods'),
  localLogin: (email: string) =>
    getItem<LoginResult>('/auth/local/login', { method: 'POST', body: { email } }),
  me: () => getItem<CurrentUser>('/auth/me'),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
};

/** The Salesforce employer pull: what each run did, and running one now. */
export const employerSync = {
  get: () => getItem<EmployerSyncOverview>('/employer-sync'),
  run: () => getItem<EmployerSync>('/employer-sync/run', { method: 'POST' }),
};

export const carrierSync = {
  get: () => getItem<EmployerSyncOverview>('/carrier-sync'),
  run: () => getItem<EmployerSync>('/carrier-sync/run', { method: 'POST' }),
};

export const reference = {
  get: () => getItem<ReferenceData>('/reference'),
};

// --- the front door ---------------------------------------------------------

export const platform = {
  /** Every application, with whether this caller may open it. Names and copy come from here. */
  apps: () => getList<Application>('/platform/apps'),
  /** The Anthropic API key: whether one is set and its last four characters, never the key. */
  modelKey: () => getItem<ModelKeyStatus>('/platform/model-key'),
  saveModelKey: (apiKey: string) =>
    getItem<ModelKeyStatus>('/platform/model-key', { method: 'PUT', body: { apiKey } }),
  removeModelKey: () => getItem<ModelKeyStatus>('/platform/model-key', { method: 'DELETE' }),
  testModelKey: () => getItem<ModelKeyCheck>('/platform/model-key/test', { method: 'POST' }),
};

// --- employers (clients) -------------------------------------------------------

export interface EmployerListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Names that begin with this, ignoring case — what a picker narrows by as somebody types. */
  namePrefix?: string;
  status?: string;
  situsState?: string;
  sort?: string;
  descending?: boolean;
}

export const employers = {
  list: (params: EmployerListParams = {}): Promise<Paged<Employer>> =>
    getCollection<Employer>('/clients', { query: { ...params } }),
  get: (id: string) => getItem<Employer>(`/clients/${id}`),
  create: (body: Record<string, unknown>) =>
    getItem<Employer>('/clients', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<Employer>(`/clients/${id}`, { method: 'PUT', body, etag }),
  archive: (id: string) => getItem<Employer>(`/clients/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => request<void>(`/clients/${id}`, { method: 'DELETE' }),
  rfps: (id: string) => getList<Rfp>(`/clients/${id}/rfps`),
  participation: (id: string) =>
    getList<{ carrierId: string; carrierName: string; timesApproached: number; statuses: Record<string, number> }>(
      `/clients/${id}/carrier-participation`,
    ),
};

// --- carriers ---------------------------------------------------------------

export const carriers = {
  list: (params: { page?: number; pageSize?: number; search?: string; status?: string } = {}) =>
    getCollection<Carrier>('/carriers', { query: { ...params } }),
  get: (id: string) => getItem<CarrierDetail>(`/carriers/${id}`),
  create: (body: Record<string, unknown>) =>
    getItem<Carrier>('/carriers', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<Carrier>(`/carriers/${id}`, { method: 'PUT', body, etag }),
  archive: (id: string) => getItem<Carrier>(`/carriers/${id}/archive`, { method: 'POST' }),
  remove: (id: string) => request<void>(`/carriers/${id}`, { method: 'DELETE' }),
  rules: (id: string) => getList<SubmissionRule>(`/carriers/${id}/submission-rules`),
  upsertRule: (id: string, body: Record<string, unknown>) =>
    getItem<SubmissionRule>(`/carriers/${id}/submission-rules`, { method: 'PUT', body }),
  deleteRule: (id: string, ruleId: string) =>
    request<void>(`/carriers/${id}/submission-rules/${ruleId}`, { method: 'DELETE' }),
  screen: (body: Record<string, unknown>) =>
    getList<EligibilityVerdict>('/carriers/screen', { method: 'POST', body }),
  contacts: (id: string, includeInactive = false) =>
    getList<CarrierContact>(`/carriers/${id}/contacts`, {
      query: includeInactive ? { includeInactive: true } : undefined,
    }),
  addContact: (id: string, body: Record<string, unknown>) =>
    getItem<CarrierContact>(`/carriers/${id}/contacts`, { method: 'POST', body }),
  updateContact: (id: string, contactId: string, body: Record<string, unknown>) =>
    getItem<CarrierContact>(`/carriers/${id}/contacts/${contactId}`, { method: 'PUT', body }),
  /** Where an RFP for this carrier goes, by state and by band of group size. */
  routes: (id: string) => getList<SubmissionRoute>(`/carriers/${id}/routes`),
  addRoute: (id: string, body: Record<string, unknown>) =>
    getItem<SubmissionRoute>(`/carriers/${id}/routes`, { method: 'POST', body }),
  updateRoute: (id: string, routeId: string, body: Record<string, unknown>) =>
    getItem<SubmissionRoute>(`/carriers/${id}/routes/${routeId}`, { method: 'PUT', body }),
  removeRoute: (id: string, routeId: string) =>
    request<void>(`/carriers/${id}/routes/${routeId}`, { method: 'DELETE' }),
  removeContact: (id: string, contactId: string) =>
    request<void>(`/carriers/${id}/contacts/${contactId}`, { method: 'DELETE' }),
};

// --- RFPs -------------------------------------------------------------------

/**
 * The address of one board cell. A carrier can be on an RFP for several lines of coverage, so
 * the line is part of the path — `/rfps/x/carriers/y` alone no longer identifies anything.
 */
const cell = (rfpId: string, carrierId: string, line: BenefitLine) =>
  `/rfps/${rfpId}/carriers/${carrierId}/lines/${encodeURIComponent(line)}`;

export interface RfpListParams {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: string;
  ownerUserId?: string;
  createdByUserId?: string;
  planYear?: number;
  dueBefore?: string;
  search?: string;
  sort?: string;
  descending?: boolean;
}

export const rfps = {
  list: (params: RfpListParams = {}) =>
    getCollection<RfpSummary>('/rfps', { query: { ...params } }),
  /** Options for the "created by" filter, shared by the dashboard and the RFP list. */
  creators: () => getList<PersonSummary>('/rfps/creators'),
  /** Who a benefit line can be handed to. Not `creators`: that is who has raised a request, so
   *  naming an owner from it means only somebody with work of their own can be given any. */
  lineOwners: () => getList<PersonSummary>('/rfps/line-owners'),
  get: (id: string) => getItem<RfpDetail>(`/rfps/${id}`),
  create: (body: Record<string, unknown>) => getItem<Rfp>('/rfps', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<Rfp>(`/rfps/${id}`, { method: 'PUT', body, etag }),
  remove: (id: string) => request<void>(`/rfps/${id}`, { method: 'DELETE' }),
  clone: (id: string, body: Record<string, unknown>) =>
    getItem<Rfp>(`/rfps/${id}/clone`, { method: 'POST', body }),
  publish: (id: string) => getItem<Rfp>(`/rfps/${id}/publish`, { method: 'POST' }),
  close: (id: string) => getItem<Rfp>(`/rfps/${id}/close`, { method: 'POST' }),
  cancel: (id: string) => getItem<Rfp>(`/rfps/${id}/cancel`, { method: 'POST' }),
  reopen: (id: string) => getItem<Rfp>(`/rfps/${id}/reopen`, { method: 'POST' }),
  archive: (id: string) => getItem<Rfp>(`/rfps/${id}/archive`, { method: 'POST' }),

  assignments: (id: string) => getList<Assignment>(`/rfps/${id}/carriers`),
  lines: (id: string) => getList<RfpLine>(`/rfps/${id}/lines`),
  /** Take a whole line off the RFP: every carrier on it, then the line. */
  removeLine: (id: string, lineId: string) =>
    request<void>(`/rfps/${id}/lines/${lineId}`, { method: 'DELETE' }),
  updateLine: (id: string, lineId: string, body: Record<string, unknown>) =>
    getItem<RfpLine>(`/rfps/${id}/lines/${lineId}`, { method: 'PUT', body }),
  /** Open lines against the person who owns each. Read-only, and read by leads. */
  workload: () => getList<Workload>('/rfps/lines/workload'),
  /** How long carriers are customarily given, by line and group size. Reference values shown
   *  beside the due-back field; the analyst applies them and nothing here returns a date. */
  dueDateGuidance: () => getList<DueDateGuidance>('/rfps/lines/due-date-guidance'),
  assignCarrier: (id: string, body: Record<string, unknown>) =>
    getItem<Assignment>(`/rfps/${id}/carriers`, { method: 'POST', body }),
  updateAssignment: (
    id: string,
    carrierId: string,
    line: BenefitLine,
    body: Record<string, unknown>,
  ) => getItem<Assignment>(cell(id, carrierId, line), { method: 'PUT', body }),
  unassignCarrier: (id: string, carrierId: string, line: BenefitLine) =>
    request<void>(cell(id, carrierId, line), { method: 'DELETE' }),
  /** With a line, a carrier that does not write it is ruled out rather than merely ranked. */
  eligibility: (id: string, benefitLine?: BenefitLine) =>
    getList<EligibilityVerdict>(`/rfps/${id}/carriers/eligibility`, {
      query: benefitLine ? { benefitLine } : {},
    }),
  setNotSelectedReason: (id: string, carrierId: string, reason: string) =>
    request<void>(`/rfps/${id}/carriers/${carrierId}/not-selected-reason`, {
      method: 'PUT',
      body: { reason },
    }),
  activity: (id: string, carrierId: string, line: BenefitLine) =>
    getList<AssignmentActivity>(`${cell(id, carrierId, line)}/activity`),
  /** A follow-up with the carrier: what they said, and the day of the check-in (today when
   *  omitted; never in the future). */
  addActivity: (
    id: string,
    carrierId: string,
    line: BenefitLine,
    message: string,
    occurredOn?: string,
  ) =>
    getItem<AssignmentActivity>(`${cell(id, carrierId, line)}/activity`, {
      method: 'POST',
      body: occurredOn ? { message, occurredOn } : { message },
    }),
  /** What a reviewer made of this carrier on this line. Per cell, not per carrier: a carrier
   *  whose dental is fine and whose vision is short is an ordinary thing to have to say. */
  reviewCell: (
    id: string,
    carrierId: string,
    line: BenefitLine,
    body: { reviewStatus: ReviewStatus; note?: string | null },
  ) => getItem<Assignment>(`${cell(id, carrierId, line)}/review`, { method: 'POST', body }),
  /** Questions asked about this carrier on this line. One raised before the board had lines
   *  carries none and shows on every line of that carrier. */
  cellClarifications: (id: string, carrierId: string, line: BenefitLine) =>
    getList<Clarification>(`${cell(id, carrierId, line)}/clarifications`),
  askCellClarification: (id: string, carrierId: string, line: BenefitLine, message: string) =>
    getItem<Clarification>(`${cell(id, carrierId, line)}/clarifications`, {
      method: 'POST',
      body: { message },
    }),
};

// --- the board --------------------------------------------------------------

export interface BoardParams {
  page?: number;
  pageSize?: number;
  /** The RFP's owner. `lineOwnerUserId` is the person working one line, which is usually
   *  somebody else — that is the reason a line has an owner of its own. */
  ownerUserId?: string;
  createdByUserId?: string;
  planYear?: number;
  search?: string;
  includeArchived?: boolean;
  /** Drafts are on by default, because the board is where a draft's cells are set up. RFP
   *  Status turns them off: it asks where everything has got to, and a draft has not
   *  started. */
  includeDrafts?: boolean;
  clientId?: string;
  benefitLine?: BenefitLine;
  lineOwnerUserId?: string;
  lineStatus?: RfpLineStatus;
  meetingFrom?: string;
  meetingTo?: string;
  /** Why each absent carrier is absent. On by default for the board; RFP Status shows no such
   *  marker and turns it off, which saves screening every carrier against every line. */
  includeScreening?: boolean;
}

export const board = {
  get: (params: BoardParams = {}) => getItem<Board>('/rfps/board', { query: { ...params } }),
};

// --- responses --------------------------------------------------------------


/**
 * What is left of the response record: the two calls that act on a clarification.
 *
 * The rest went with the Responses pages. A carrier's response is one record per carrier per
 * RFP, which is the wrong grain for everything the board does — a carrier whose dental was
 * fine and whose vision was short had one status for both — so reviewing, reopening, notes
 * and the status itself are all per line on the cell now. These two survive because a
 * clarification is addressed by its own id and was never really about the response.
 */
export const responses = {
  answerClarification: (clarificationId: string, answer: string) =>
    getItem<Clarification>(`/clarifications/${clarificationId}`, {
      method: 'PUT',
      body: { answer },
    }),
  resolveClarification: (clarificationId: string) =>
    getItem<Clarification>(`/clarifications/${clarificationId}/resolve`, { method: 'POST' }),
};

// --- dashboards -------------------------------------------------------------

export const dashboards = {
  internal: (params: { clientId?: string; ownerUserId?: string; planYear?: number } = {}) =>
    getItem<InternalDashboard>('/dashboard/internal', { query: { ...params } }),
  client: (clientId: string) => getItem<Record<string, unknown>>(`/dashboard/clients/${clientId}`),
  followUps: (staleAfterDays = 7) =>
    getList<FollowUp>('/dashboard/follow-ups', { query: { staleAfterDays } }),
};

// --- users ------------------------------------------------------------------

export interface ProposalJobParams {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: string;
  rfpId?: string;
  /** One term over the comparison's name and the employer's. */
  q?: string;
}

export const proposals = {
  formats: () => getList<DocumentFormat>('/proposals/formats'),
  readModels: () => getList<string>('/proposals/read-models'),
  /** What analysts corrected of what was read; `source` narrows to the model's or the readers'. */
  readAccuracy: (source?: 'model' | 'reader') =>
    getItem<ReadAccuracy>('/proposals/read-accuracy', { query: source ? { source } : {} }),
  batchReads: () => getItem<boolean>('/proposals/batch-reads'),
  list: (params: ProposalJobParams = {}) =>
    getCollection<ExtractionJobSummary>('/proposals/jobs', { query: { ...params } }),
  get: (id: string) => getItem<ExtractionJobDetail>(`/proposals/jobs/${id}`),
  batchProgress: (id: string) => getItem<BatchProgress | null>(`/proposals/jobs/${id}/batch`),
  create: (body: Record<string, unknown>) =>
    getItem<ExtractionJob>('/proposals/jobs', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<ExtractionJob>(`/proposals/jobs/${id}`, { method: 'PUT', body, etag }),
  remove: (id: string) => request<void>(`/proposals/jobs/${id}`, { method: 'DELETE' }),

  /** Multipart, so the body is a FormData rather than JSON. */
  uploadDocument: (id: string, file: File) => {
    const form = new FormData();
    // Named explicitly: a file from a chosen folder is otherwise sent under its path in the folder.
    form.append('file', file, file.name);
    return getItem<ExtractionPlan>(`/proposals/jobs/${id}/documents`, {
      method: 'POST',
      formData: form,
    });
  },
  removeDocument: (id: string, planId: string) =>
    request<void>(`/proposals/jobs/${id}/documents/${planId}`, { method: 'DELETE' }),
  /** A first placement of the SBCs and sold proposals from their names. Moves nothing placed. */
  placeByName: (id: string) =>
    getItem<PlacedByName>(`/proposals/jobs/${id}/place-by-name`, { method: 'POST' }),
  updatePlan: (id: string, planId: string, body: Record<string, unknown>) =>
    getItem<ExtractionPlan>(`/proposals/jobs/${id}/plans/${planId}`, { method: 'PUT', body }),
  /** `values` corrects; `accept` says the reader got these fields right and clears their flags
   *  without touching what they say. */
  reviewPlan: (id: string, planId: string, values: Record<string, unknown>, accept: string[] = []) =>
    getItem<ExtractionPlan>(`/proposals/jobs/${id}/plans/${planId}/review`, {
      method: 'POST',
      body: { values, accept },
    }),
  /** The whole table for a document a format reads as one — a sold proposal's plans. Sent entire
   *  rather than as a patch, because a row can be added and removed as well as corrected. */
  savePlanRows: (id: string, planId: string, rows: Record<string, unknown>[]) =>
    getItem<ExtractionPlan>(`/proposals/jobs/${id}/plans/${planId}/review`, {
      method: 'POST',
      body: { rows },
    }),
  extract: (id: string) =>
    getItem<ExtractionJob>(`/proposals/jobs/${id}/extract`, { method: 'POST' }),
  /** These documents read again from the start: what was read from them, corrections and
   *  ticked plans included, is replaced. */
  readAgain: (id: string, planIds: string[]) =>
    getItem<ExtractionJob>(`/proposals/jobs/${id}/read-again`, {
      method: 'POST',
      body: { planIds },
    }),
  generate: (id: string) =>
    getItem<ExtractionJob>(`/proposals/jobs/${id}/generate`, { method: 'POST' }),
  /** The tenant's library, ordered by format then name — the order a dropdown shows. */
  templates: () => getList<ExtractionTemplate>('/proposals/templates'),
  /** Multipart: the workbook plus the fields that name it. Needs proposal_template:write. */
  addTemplate: ({
    formatId,
    scope,
    name,
    file,
  }: {
    formatId: string;
    scope: ComparisonScope;
    name: string;
    file: File;
  }) => {
    const form = new FormData();
    form.append('formatId', formatId);
    form.append('scope', scope);
    form.append('name', name);
    form.append('file', file);
    return getItem<ExtractionTemplate>('/proposals/templates', { method: 'POST', formData: form });
  },
  setDefaultTemplate: (id: string) =>
    getItem<ExtractionTemplate>(`/proposals/templates/${id}/default`, { method: 'POST' }),
  /** Leave the library with no default, which is a state it is allowed to be in. */
  clearDefaultTemplate: (id: string) =>
    getItem<ExtractionTemplate>(`/proposals/templates/${id}/default`, { method: 'DELETE' }),
  removeTemplate: (id: string) => request<void>(`/proposals/templates/${id}`, { method: 'DELETE' }),
  downloadTemplate: (id: string, fileName: string) =>
    downloadFile(`/proposals/templates/${id}/file`, fileName),

  /**
   * The deliverables, fetched with the caller's token.
   *
   * Not a plain `href`: the access token lives in memory rather than in local storage, so a
   * browser navigation to this route carries no Authorization header and the API answers 401.
   * The page is opened to be read; the workbook is saved.
   */
  openPage: (id: string) =>
    openFile(`/proposals/jobs/${id}/download/page`, 'Health Comparison.html'),
  /** The same page through the print window, where Save as PDF makes the PDF. */
  printPagePdf: (id: string) => printFile(`/proposals/jobs/${id}/download/page`),
  /**
   * Named "{comparison name} - Comparison.xlsx". The API names it too, but in Azure the download is
   * a redirect to signed storage whose headers the page cannot read, so the name given here is
   * the one the file gets there. Same cleaning as the API's `workbook_download_name`.
   */
  downloadWorkbook: (id: string, comparisonName: string, index?: number, templateName?: string) => {
    const cleaned = comparisonName
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/^[ .-]+|[ .-]+$/g, '');
    // One of several: an ancillary comparison writes a workbook per file its plan types need,
    // each named after its template so three downloads are not three of one name.
    if (index !== undefined) {
      const name = [cleaned, templateName].filter(Boolean).join(' - ');
      return downloadFile(
        `/proposals/jobs/${id}/download/workbook-${index}`,
        `${name || 'Comparison'}.xlsx`,
      );
    }
    return downloadFile(
      `/proposals/jobs/${id}/download/workbook`,
      cleaned ? `${cleaned} - Comparison.xlsx` : 'Comparison.xlsx',
    );
  },
};

export const users = {
  list: (
    params: { page?: number; pageSize?: number; status?: string; search?: string } = {},
  ) => getCollection<User>('/users', { query: { ...params } }),
  get: (id: string) => getItem<User>(`/users/${id}`),
  create: (body: Record<string, unknown>) => getItem<User>('/users', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<User>(`/users/${id}`, { method: 'PUT', body, etag }),
  activate: (id: string) => getItem<User>(`/users/${id}/activate`, { method: 'POST' }),
  deactivate: (id: string) => getItem<User>(`/users/${id}/deactivate`, { method: 'POST' }),
  remove: (id: string) => request<void>(`/users/${id}`, { method: 'DELETE' }),
};

// --- audit and exports ------------------------------------------------------

export const audit = {
  list: (
    params: {
      page?: number;
      pageSize?: number;
      action?: string;
      entityType?: string;
      entityId?: string;
      userId?: string;
      since?: string;
      until?: string;
    } = {},
  ) => getCollection<AuditEvent>('/audit-events', { query: { ...params } }),
  get: (id: string) => getItem<AuditEvent>(`/audit-events/${id}`),
};

export const exports = {
  rfpStatus: (rfpId: string) =>
    downloadFile(`/exports/rfps/${rfpId}/status`, 'rfp-status.csv'),
  responses: (params: { clientId?: string; carrierId?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.clientId) query.set('clientId', params.clientId);
    if (params.carrierId) query.set('carrierId', params.carrierId);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return downloadFile(`/exports/responses${suffix}`, 'responses.csv');
  },
  audit: (since?: string, until?: string) => {
    const query = new URLSearchParams();
    if (since) query.set('since', since);
    if (until) query.set('until', until);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return downloadFile(`/exports/audit-events${suffix}`, 'audit.csv');
  },
};

// --- data trenching ---------------------------------------------------------

export interface TrenchingEngagementParams {
  page?: number;
  pageSize?: number;
  clientId?: string;
  variation?: string;
  status?: string;
}

export const trenching = {
  list: (params: TrenchingEngagementParams = {}) =>
    getCollection<TrenchingEngagementSummary>('/trenching/engagements', { query: { ...params } }),
  get: (id: string) => getItem<TrenchingEngagementDetail>(`/trenching/engagements/${id}`),
  create: (body: Record<string, unknown>) =>
    getItem<TrenchingEngagement>('/trenching/engagements', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<TrenchingEngagement>(`/trenching/engagements/${id}`, { method: 'PUT', body, etag }),
  remove: (id: string) => request<void>(`/trenching/engagements/${id}`, { method: 'DELETE' }),
  /** Every default as this tenant sees it. Never paginated. */
  assumptions: () => getList<TrenchingAssumption>('/trenching/assumptions'),
  /** Needs trenching_assumptions:write. Only the keys named change. */
  updateAssumptions: (values: Record<string, number>) =>
    getList<TrenchingAssumption>('/trenching/assumptions', { method: 'PUT', body: { values } }),

  /** The three workbook families. Never paginated. */
  families: () => getList<TemplateFamily>('/trenching/families'),
  /** The tenant's library, ordered by family then name — the order a dropdown shows. */
  templates: () => getList<TrenchingTemplate>('/trenching/templates'),
  /** Multipart: the workbook plus the family and name that place it. Needs trenching_template:write. */
  addTemplate: ({ familyId, name, file }: { familyId: string; name: string; file: File }) => {
    const form = new FormData();
    form.append('familyId', familyId);
    form.append('name', name);
    form.append('file', file);
    return getItem<TrenchingTemplate>('/trenching/templates', { method: 'POST', formData: form });
  },
  setDefaultTemplate: (id: string) =>
    getItem<TrenchingTemplate>(`/trenching/templates/${id}/default`, { method: 'POST' }),
  clearDefaultTemplate: (id: string) =>
    getItem<TrenchingTemplate>(`/trenching/templates/${id}/default`, { method: 'DELETE' }),
  removeTemplate: (id: string) => request<void>(`/trenching/templates/${id}`, { method: 'DELETE' }),
  downloadTemplate: (id: string, fileName: string) =>
    downloadFile(`/trenching/templates/${id}/file`, fileName),

  /** Multipart: the file plus its declared kind, and the carrier for a census. */
  uploadFile: (
    id: string,
    file: File,
    kind: TrenchingArtifactKind,
    carrierId?: string | null,
  ) => {
    const form = new FormData();
    form.append('kind', kind);
    if (carrierId) form.append('carrierId', carrierId);
    form.append('file', file);
    return getItem<TrenchingArtifact>(`/trenching/engagements/${id}/files`, {
      method: 'POST',
      formData: form,
    });
  },
  removeFile: (id: string, artifactId: string) =>
    request<void>(`/trenching/engagements/${id}/files/${artifactId}`, { method: 'DELETE' }),
  /** Queue the workbook. Needs trenching:generate; the page watches the status. */
  generate: (id: string) =>
    getItem<TrenchingEngagement>(`/trenching/engagements/${id}/generate`, { method: 'POST' }),
  downloadWorkbook: (id: string, fileName: string) =>
    downloadFile(`/trenching/engagements/${id}/workbook`, fileName),
  ingest: (id: string) =>
    getItem<TrenchingEngagement>(`/trenching/engagements/${id}/ingest`, { method: 'POST' }),
  members: (id: string, params: { page?: number; pageSize?: number } = {}) =>
    getCollection<TrenchingMember>(`/trenching/engagements/${id}/members`, {
      query: { ...params },
    }),
  predecessors: (clientId: string, ordinal: number) =>
    getList<TrenchingEngagementSummary>('/trenching/engagements/predecessors', {
      query: { clientId, ordinal },
    }),
  memberItems: (id: string, memberId: string) =>
    getList<TrenchingMemberItem>(`/trenching/engagements/${id}/members/${memberId}/items`),
  markItem: (id: string, memberId: string, itemId: string, affectsPlanYear: string | null) =>
    getItem<TrenchingMemberItem>(
      `/trenching/engagements/${id}/members/${memberId}/items/${itemId}`,
      { method: 'PUT', body: { affectsPlanYear } },
    ),
  resolveFlag: (id: string, memberId: string, resolution: FlagResolution) =>
    getItem<TrenchingMember>(`/trenching/engagements/${id}/members/${memberId}/resolve`, {
      method: 'POST',
      body: { resolution },
    }),

  /** Every override on an engagement, oldest first. Never paginated. */
  adjustments: (id: string) =>
    getList<TrenchingAdjustment>(`/trenching/engagements/${id}/adjustments`),
  /** Needs trenching:review. A reason is required by the API, not only by the form. */
  addAdjustment: (id: string, body: Record<string, unknown>) =>
    getItem<TrenchingAdjustment>(`/trenching/engagements/${id}/adjustments`, {
      method: 'POST',
      body,
    }),
  updateAdjustment: (id: string, adjustmentId: string, body: Record<string, unknown>) =>
    getItem<TrenchingAdjustment>(`/trenching/engagements/${id}/adjustments/${adjustmentId}`, {
      method: 'PUT',
      body,
    }),
  removeAdjustment: (id: string, adjustmentId: string) =>
    request<void>(`/trenching/engagements/${id}/adjustments/${adjustmentId}`, {
      method: 'DELETE',
    }),
};

// --- census creation --------------------------------------------------------

export const census = {
  list: (
    params: { page?: number; pageSize?: number; clientId?: string; batchStatus?: string } = {},
  ) => getCollection<CensusBatchSummary>('/census', { query: { ...params } }),
  get: (id: string) => getItem<CensusBatchDetail>(`/census/${id}`),
  create: (body: Record<string, unknown>) =>
    getItem<CensusBatch>('/census', { method: 'POST', body }),
  update: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<CensusBatch>(`/census/${id}`, { method: 'PUT', body, etag }),
  remove: (id: string) => request<void>(`/census/${id}`, { method: 'DELETE' }),

  /** The whole census at once: the grid's unit of work is the sheet, not the row.
   *
   *  A member whose `ssnMasked` is true must be sent back with its `ssn` exactly as it arrived.
   *  The backend reads that as "unchanged" and keeps the stored number; anything else saves the
   *  mask over it, and the number is then gone. */
  saveMembers: (id: string, members: Record<string, unknown>[]) =>
    getList<CensusMember>(`/census/${id}/members`, { method: 'PUT', body: { members } }),

  /** People and their conditions from a Form Fire medical-condition export, merged into the
   *  census: somebody already on it, by SSN, is updated rather than added twice. */
  importFormFire: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<FormFireImport>(`/census/${id}/formfire`, { method: 'POST', formData: form });
  },

  /** What an employer's census workbook appears to hold, column by column. Nothing is stored. */
  inspectSpreadsheet: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<SpreadsheetInspect>(`/census/${id}/spreadsheet/inspect`, {
      method: 'POST',
      formData: form,
    });
  },
  /** The workbook's people, read by the mapping the analyst confirmed, merged into the census.
   *  A column the mapping leaves out is not read. */
  importSpreadsheet: (id: string, file: File, mapping: Record<number, string>) => {
    const form = new FormData();
    form.append('file', file);
    form.append('mapping', JSON.stringify(mapping));
    return getItem<SpreadsheetImport>(`/census/${id}/spreadsheet`, {
      method: 'POST',
      formData: form,
    });
  },

  generate: (id: string) => getItem<CensusBatch>(`/census/${id}/generate`, { method: 'POST' }),
  download: (id: string, path: string, fileName: string) =>
    downloadFile(`/census/${id}/download?path=${encodeURIComponent(path)}`, fileName),

  /** Every carrier and whether a census can be produced for it, with the reason when not. */
  carriers: () => getList<CensusCarrierChoice>('/census/carriers'),

  /** Every carrier this application can write for, and whether its template is uploaded. */
  layouts: () => getList<CensusCarrierLayout>('/census/layouts'),
  /** Every field a column can be mapped to, with the values a coded one takes. */
  fields: () => getList<CensusField>('/census/fields'),

  /** The layouts this tenant has defined for carriers the application has no code for. */
  definedLayouts: () => getList<CensusLayoutDefinition>('/census/layouts/defined'),
  definedLayout: (id: string) => getItem<CensusLayoutDefinition>(`/census/layouts/defined/${id}`),
  defineLayout: (body: Record<string, unknown>) =>
    getItem<CensusLayoutDefinition>('/census/layouts/defined', { method: 'POST', body }),
  updateLayout: (id: string, body: Record<string, unknown>, etag?: string) =>
    getItem<CensusLayoutDefinition>(`/census/layouts/defined/${id}`, {
      method: 'PUT',
      body,
      etag,
    }),
  removeLayout: (id: string) => request<void>(`/census/layouts/defined/${id}`, { method: 'DELETE' }),

  /** Read a workbook and propose what its columns hold. Nothing is stored: this is the mapping
   *  screen filling itself in, and the analyst's confirmation is what makes it real. */
  inspectWorkbook: (file: File, sheet?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (sheet) form.append('sheet', sheet);
    return getItem<InferredLayout>('/census/layouts/defined/inspect', {
      method: 'POST',
      formData: form,
    });
  },

  /** Multipart. Refused with the reason when the workbook is not that carrier's template. */
  uploadTemplate: (layoutKey: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<CensusCarrierLayout>(`/census/layouts/${layoutKey}/template`, {
      method: 'POST',
      formData: form,
    });
  },
};

// --- presentation creation ------------------------------------------------------

export interface PresentationDeckParams {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: string;
  /** Only decks assigned to the signed-in user. */
  assignedToMe?: boolean;
  /** Only decks assigned to this person. */
  assignedToUserId?: string;
  /** Only decks at this point of review. */
  reviewStatus?: string;
}

export interface DeckCreateInput {
  clientId: string;
  name: string;
  template: DeckTemplate | '';
  options: DeckOptions;
  workbook: File;
  comparison?: File | null;
  logo?: File | null;
  /** The logo's Cloudinary URL, instead of a file; the server fetches it once. */
  logoUrl?: string;
  /** Who the deck is assigned to. Omitted, the API assigns it to whoever creates it. */
  assignedToUserId?: string;
}

export interface PacketPageInput {
  title: string;
  fields: Record<string, string>;
  formats?: Record<string, PacketFieldFormat>;
  /** Left out, the page has no background and no placed images: the editor sends the whole page. */
  layout?: PacketPageLayout;
}

/** Employee packet creation. Every write carries the packet's etag: pages are saved one at a
 *  time, but always as part of their packet, so two people cannot lose each other's work. */
export const packets = {
  library: () => getItem<PacketLibrary>('/packets/library'),
  list: (params: PacketListParams = {}) => getCollection<Packet>('/packets', { query: { ...params } }),
  get: (id: string) => getItem<PacketDetail>(`/packets/${id}`),
  create: (body: { clientId: string; name: string; template: string; settings?: Record<string, string> }) =>
    getItem<PacketDetail>('/packets', { method: 'POST', body }),
  update: (
    id: string,
    body: { name?: string; template?: string; settings?: Record<string, string> },
    etag?: string,
  ) => getItem<PacketDetail>(`/packets/${id}`, { method: 'PUT', body, etag }),
  remove: (id: string) => request<void>(`/packets/${id}`, { method: 'DELETE' }),
  addPage: (id: string, template: string, afterPageId: string | undefined, etag?: string) =>
    getItem<{ packet: PacketDetail; pageId: string }>(`/packets/${id}/pages`, {
      method: 'POST',
      body: { template, afterPageId },
      etag,
    }),
  /** The page saved; `cleaned` names the HTML fields the server removed something from. */
  savePage: (id: string, pageId: string, body: PacketPageInput, etag?: string) =>
    getItem<{ packet: PacketDetail; cleaned: string[] }>(`/packets/${id}/pages/${pageId}`, {
      method: 'PUT',
      body,
      etag,
    }),
  duplicatePage: (id: string, pageId: string, etag?: string) =>
    getItem<{ packet: PacketDetail; pageId: string }>(`/packets/${id}/pages/${pageId}/duplicate`, {
      method: 'POST',
      etag,
    }),
  removePage: (id: string, pageId: string, etag?: string) =>
    getItem<PacketDetail>(`/packets/${id}/pages/${pageId}`, { method: 'DELETE', etag }),
  /** A page switched on (in the printed packet) or off (kept, but left out of it). */
  switchPage: (id: string, pageId: string, included: boolean, etag?: string) =>
    getItem<PacketDetail>(`/packets/${id}/pages/${pageId}/included`, {
      method: 'PUT',
      body: { included },
      etag,
    }),
  reorder: (id: string, pageIds: string[], etag?: string) =>
    getItem<PacketDetail>(`/packets/${id}/pages/order`, { method: 'PUT', body: { pageIds }, etag }),
  /** One page as it prints, with the editor's unsaved fields when given. Nothing is saved. */
  previewPage: (id: string, pageId: string, body?: PacketPageInput) =>
    getItem<{ html: string }>(`/packets/${id}/pages/${pageId}/preview`, { method: 'POST', body }),
  uploadImage: (id: string, file: File, etag?: string) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<{ packet: PacketDetail; assetId: string }>(`/packets/${id}/images`, {
      method: 'POST',
      formData: form,
      etag,
    });
  },
  /** The sample HTML pages, and one of them saved as a file. */
  htmlSamples: () => getList<PacketHtmlSample>('/packets/samples'),
  downloadHtmlSample: (sample: PacketHtmlSample) =>
    downloadFile(`/packets/samples/${encodeURIComponent(sample.id)}`, sample.fileName),
  /** A pre-built .html file as a new HTML page, after the page named or at the end. */
  addHtmlPage: (id: string, file: File, afterPageId: string | undefined, etag?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (afterPageId) form.append('afterPageId', afterPageId);
    return getItem<{
      packet: PacketDetail;
      pageId: string;
      title: string;
      cleaned: boolean;
      /** External files the page refers to that could not be brought in, and why. */
      leftOut: { url: string; reason: string }[];
    }>(
      `/packets/${id}/pages/html`,
      { method: 'POST', formData: form, etag },
    );
  },
  /** A page from the library as a new page, after the page named or at the end: uploaded into
   *  the packet as an .html file is, under the library's name. */
  addLibraryPage: (id: string, libraryPageId: string, afterPageId: string | undefined, etag?: string) =>
    getItem<{
      packet: PacketDetail;
      pageId: string;
      title: string;
      cleaned: boolean;
      leftOut: { url: string; reason: string }[];
    }>(`/packets/${id}/pages/library`, { method: 'POST', body: { libraryPageId, afterPageId }, etag }),
  /** The page library: pages an administrator adds, which any packet can add. */
  libraryPages: () => getList<PacketLibraryPage>('/packets/library-pages'),
  /** Multipart: the .html and its name. Needs packet_template:write. */
  createLibraryPage: ({ name, file }: { name: string; file: File }) => {
    const form = new FormData();
    form.append('name', name);
    form.append('file', file);
    return getItem<PacketLibraryPage>('/packets/library-pages', { method: 'POST', formData: form });
  },
  /** An edited file in a library page's place, keeping its name. Needs packet_template:write. */
  replaceLibraryPage: (pageId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<PacketLibraryPage>(`/packets/library-pages/${encodeURIComponent(pageId)}/file`, {
      method: 'POST',
      formData: form,
    });
  },
  removeLibraryPage: (pageId: string) =>
    request<void>(`/packets/library-pages/${encodeURIComponent(pageId)}`, { method: 'DELETE' }),
  downloadLibraryPage: (pageId: string, fileName: string) =>
    downloadFile(`/packets/library-pages/${encodeURIComponent(pageId)}/file`, fileName),
  removeImage: (id: string, assetId: string, etag?: string) =>
    getItem<PacketDetail>(`/packets/${id}/images/${assetId}`, { method: 'DELETE', etag }),
  image: (id: string, assetId: string) =>
    fetchBlob(`/packets/${encodeURIComponent(id)}/images/${encodeURIComponent(assetId)}`),
  /** The text of a .txt or .docx for a field; for an HTML field, also an .html file, cleaned,
   *  and the others as HTML. Nothing is stored. */
  importText: (id: string, file: File, asHtml = false) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<{ text: string; truncated: boolean }>(`/packets/${id}/text-import`, {
      method: 'POST',
      formData: form,
      query: asHtml ? { asHtml: true } : undefined,
    });
  },
  document: (id: string) => fetchBlob(`/packets/${id}/document`),
  download: (id: string, fileName: string) => downloadFile(`/packets/${id}/document?download=true`, fileName),
  /** The packet in the browser's print window, one page to a sheet, where Save as PDF writes it. */
  printPdf: (id: string) => printFile(`/packets/${id}/document`),
  reviewers: (clientId: string) => getList<PersonSummary>('/packets/reviewers', { query: { clientId } }),
  assignees: () => getList<PersonSummary>('/packets/assignees'),
  sendForReview: (id: string, reviewerUserId: string, etag?: string) =>
    getItem<PacketDetail>(`/packets/${id}/review-request`, { method: 'POST', body: { reviewerUserId }, etag }),
  decideReview: (id: string, decision: 'Approved' | 'Rejected', note: string, etag?: string) =>
    getItem<PacketDetail>(`/packets/${id}/review-decision`, { method: 'POST', body: { decision, note }, etag }),
};

export const presentations = {
  list: (params: PresentationDeckParams = {}) =>
    getCollection<PresentationDeck>('/presentations/decks', { query: { ...params } }),
  get: (id: string) => getItem<PresentationDeckDetail>(`/presentations/decks/${id}`),
  /** Multipart: the workbook, optional comparison and logo, and the options as one JSON field.
   *  The deck is generated before the response arrives. */
  create: (input: DeckCreateInput) => {
    const form = new FormData();
    form.append('clientId', input.clientId);
    form.append('name', input.name);
    if (input.template) form.append('template', input.template);
    form.append('options', JSON.stringify(input.options));
    form.append('workbook', input.workbook);
    if (input.comparison) form.append('comparison', input.comparison);
    if (input.logo) form.append('logo', input.logo);
    if (input.logoUrl) form.append('logoUrl', input.logoUrl);
    if (input.assignedToUserId) form.append('assignedToUserId', input.assignedToUserId);
    return getItem<PresentationDeckDetail>('/presentations/decks', {
      method: 'POST',
      formData: form,
    });
  },
  /** New options; the deck regenerates. */
  /** Who a deck for this employer can be assigned to: people who can work it. */
  assignees: (clientId?: string) =>
    getList<PersonSummary>('/presentations/assignees', { query: clientId ? { clientId } : {} }),
  /** Who a deck for this employer can be sent to for review. */
  reviewers: (clientId: string) => getList<PersonSummary>('/presentations/reviewers', { query: { clientId } }),
  /** The deck, sent to a reviewer: theirs alone to change until they decide. */
  sendForReview: (id: string, reviewerUserId: string, etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/review-request`, {
      method: 'POST',
      body: { reviewerUserId },
      etag,
    }),
  /** The reviewer's decision; the deck goes back to whoever sent it. A rejection needs a note. */
  decideReview: (id: string, decision: 'Approved' | 'Rejected', note: string, etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/review-decision`, {
      method: 'POST',
      body: { decision, note },
      etag,
    }),
  update: (
    id: string,
    body: {
      name?: string;
      assignedToUserId?: string;
      options: DeckOptions;
    },
    etag?: string,
  ) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}`, { method: 'PUT', body, etag }),
  remove: (id: string) => request<void>(`/presentations/decks/${id}`, { method: 'DELETE' }),
  generate: (id: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/generate`, { method: 'POST' }),
  /** Every text edit the HTML deck should carry, replacing those it had; the deck regenerates. */
  saveTextEdits: (id: string, edits: DeckTextEdit[], etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/text-edits`, {
      method: 'PUT',
      body: { edits },
      etag,
    }),
  /** How each tab of each workbook the deck is built from was read, for the Check step. */
  reading: (id: string) => getList<WorkbookReading>(`/presentations/decks/${id}/reading`),
  /** One tab's cells, for picking where a part of it is. */
  workbookGrid: (id: string, kind: WorkbookKind, tab: string) =>
    getItem<WorkbookGrid>(`/presentations/decks/${id}/workbooks/${kind}/grid?tab=${encodeURIComponent(tab)}`),
  /** A tab's correction for the deck's employer; the deck regenerates. */
  saveReadingCorrection: (id: string, kind: WorkbookKind, body: ReadingCorrectionInput, etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/workbooks/${kind}/corrections`, {
      method: 'PUT',
      body,
      etag,
    }),
  /** The tab read again as the reader finds it; the deck regenerates. */
  clearReadingCorrection: (id: string, kind: WorkbookKind, tab: string, etag?: string) =>
    getItem<PresentationDeckDetail>(
      `/presentations/decks/${id}/workbooks/${kind}/corrections?tab=${encodeURIComponent(tab)}`,
      { method: 'DELETE', etag },
    ),
  /** The workbook tabs an added slide may read — visible ones, never Trenching. */
  workbookSheets: (id: string) => getList<WorkbookSheet>(`/presentations/decks/${id}/workbook-sheets`),
  /** A slide of the analyst's own, filled from the workbook; the deck regenerates. */
  addSlide: (id: string, body: AddedSlideInput, etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/added-slides`, {
      method: 'POST',
      body,
      etag,
    }),
  changeSlide: (id: string, addedId: string, body: AddedSlideInput, etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/added-slides/${addedId}`, {
      method: 'PUT',
      body,
      etag,
    }),
  deleteSlide: (id: string, addedId: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/added-slides/${addedId}`, {
      method: 'DELETE',
    }),
  /** Every slide the deck should leave out, by key; any not listed is restored. Regenerates. */
  /** Every finding marked resolved, replacing the set; the deck does not regenerate. */
  setResolvedFindings: (id: string, findings: string[], etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/resolved-findings`, {
      method: 'PUT',
      body: { findings },
      etag,
    }),
  setRemovedSlides: (id: string, slides: string[], etag?: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/removed-slides`, {
      method: 'PUT',
      body: { slides },
      etag,
    }),
  /** Back to the generated wording everywhere; the deck regenerates. */
  resetTextEdits: (id: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/text-edits`, { method: 'DELETE' }),
  /** A replacement workbook, comparison or logo; the deck regenerates. */
  replaceFile: (id: string, kind: DeckFileKind, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<PresentationDeckDetail>(`/presentations/decks/${id}/files/${kind}`, {
      method: 'PUT',
      formData: form,
    });
  },
  /** The logo from its Cloudinary URL, replacing the deck's; the deck regenerates. */
  setLogoUrl: (id: string, url: string) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/logo-url`, {
      method: 'PUT',
      body: { url },
    }),
  removeFile: (id: string, kind: DeckFileKind) =>
    getItem<PresentationDeckDetail>(`/presentations/decks/${id}/files/${kind}`, {
      method: 'DELETE',
    }),
  /** The deck in a new tab, to read or print to PDF. */
  open: (id: string) => openFile(`/presentations/decks/${id}/deck`, 'presentation.html'),
  /** The deck as a PDF: the HTML deck in the browser's print dialog, one slide to a page —
   *  recorded as a PDF download. */
  printPdf: (id: string) => printFile(`/presentations/decks/${id}/deck?purpose=pdf`),
  download: (id: string, fileName: string) =>
    downloadFile(`/presentations/decks/${id}/deck`, fileName),
  /** The deck as a blob, for the in-page preview. */
  /** The deck as HTML: its HTML design when its family has one; `app` asks for the
   *  application's own layout, which the in-place editor edits. */
  fetchDeck: (id: string, layout: 'auto' | 'app' = 'auto') =>
    fetchBlob(`/presentations/decks/${id}/deck${layout === 'app' ? '?layout=app' : ''}`),
  /** An HTML design: made the default, removed, or downloaded, by its id. */
  setDefaultTemplate: (id: string) =>
    getItem<TrenchingTemplate>(`/presentations/templates/${id}/default`, { method: 'POST' }),
  removeTemplate: (id: string) =>
    request<void>(`/presentations/templates/${id}`, { method: 'DELETE' }),
  downloadTemplate: (id: string, fileName: string) =>
    downloadFile(`/presentations/templates/${id}/file`, fileName),

  /** The HTML design families, one per deck template. Never paginated. */
  htmlFamilies: () => getList<TemplateFamily>('/presentations/html-families'),
  htmlTemplates: () => getList<TrenchingTemplate>('/presentations/html-templates'),
  /** Multipart: the .html plus the family and name. Needs presentation_template:write. Making one
   *  the default, removing and downloading it use the template routes above. */
  addHtmlTemplate: ({ familyId, name, file }: { familyId: string; name: string; file: File }) => {
    const form = new FormData();
    form.append('familyId', familyId);
    form.append('name', name);
    form.append('file', file);
    return getItem<TrenchingTemplate>('/presentations/html-templates', {
      method: 'POST',
      formData: form,
    });
  },
  /** An HTML design's file replaced by an edited one: its name and default stay. Checked as an
   *  upload is, and refused with every reason. Needs presentation_template:write. */
  replaceHtmlTemplate: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return getItem<TrenchingTemplate>(`/presentations/html-templates/${id}/file`, {
      method: 'POST',
      formData: form,
    });
  },
  /** The built-in design to restyle and upload back: SPY1's, which shows the contract. */
  downloadHtmlStarter: (familyId: string) =>
    downloadFile(`/presentations/html-templates/starter/${familyId}`, `${familyId}-starter.html`),
};
